import React, { useEffect, useRef, useState } from "react";

import { accountConfirmationTranslations } from "common/i18n/account-confirmation/translations";
import { isMessageKey } from "common/i18n/types";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import { Header } from "../components/layout/header";
import { RedirectProgressBar } from "../components/redirect-progress-bar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../contexts/auth-context";
import { useTranslations } from "../hooks/use-translations";

import type { RedirectProgressBarHandle } from "../components/redirect-progress-bar";

export const ConfirmAccountPage = (): React.JSX.Element => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(``);
  const [success, setSuccess] = useState(``);
  const [showRedirect, setShowRedirect] = useState(false);
  const [redirectStarted, setRedirectStarted] = useState(false);
  const redirectRef = useRef<RedirectProgressBarHandle | null>(null);

  const { confirmAccount, isAuthenticated } = useAuth();

  const { translations, language } = useTranslations();
  const confirmTranslations = accountConfirmationTranslations[language];

  const email = searchParams.get(`email`);
  const confirmAccountToken = searchParams.get(`confirmAccountToken`);
  const simulate = searchParams.get(`simulate`) === `true`;

  useEffect(() => {
    const handleConfirmation = async () => {
      if (simulate) {
        setSuccess(confirmTranslations.success);
        setShowRedirect(true);
        setRedirectStarted(false);
        setIsLoading(false);
        return;
      }

      if (!email || !confirmAccountToken) {
        setError(translations.common.invalidConfirmationLink);
        setIsLoading(false);
        return;
      }

      try {
        await confirmAccount(email, confirmAccountToken);
        setSuccess(confirmTranslations.success);
        setShowRedirect(true);
        setRedirectStarted(false);
      } catch (err) {
        if (err instanceof Error) {
          const MessageKey = err.message;
          const translated = isMessageKey(MessageKey)
            ? translations.auth[MessageKey]
            : translations.confirmAccount.error;

          setError(translated);
        } else {
          setError(translations.confirmAccount.error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    handleConfirmation();
  }, [email, confirmAccountToken, confirmAccount, confirmTranslations, translations, simulate]);

  if (isAuthenticated) {
    return <Navigate replace to="/account/data" />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card data-testid="confirm-account-card">
            <CardHeader>
              <CardTitle className="text-center">{confirmTranslations.title}</CardTitle>
              <CardDescription className="text-center">
                {isLoading ? translations.confirmAccount.confirming : translations.confirmAccount.status}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              {isLoading ? (
                <div className="text-gray-600 dark:text-gray-400" data-testid="confirm-loading">
                  {translations.confirmAccount.waiting}
                </div>
              ) : null}

              {error ? (
                <div
                  className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
                  data-testid="confirm-error"
                >
                  {error}
                </div>
              ) : null}

              {success ? (
                <div
                  className="text-green-600 dark:text-green-400 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-md"
                  data-testid="confirm-success"
                >
                  {success}
                </div>
              ) : null}

              <div className="space-y-2">
                {showRedirect ? (
                  <RedirectProgressBar
                    ref={(instance) => {
                      redirectRef.current = instance;

                      if (instance && showRedirect && !redirectStarted) {
                        instance.start();
                        setRedirectStarted(true);
                      }
                    }}
                    redirectUrl="/login"
                    title={translations.redirects.toLoginAfterConfirmTitle}
                    onCancel={() => {
                      setShowRedirect(false);
                      setRedirectStarted(false);
                    }}
                  />
                ) : null}

                <Link to="/login">
                  <Button className="w-full">{translations.common.continueToSignIn}</Button>
                </Link>

                {error ? (
                  <Link to="/register">
                    <Button className="w-full" variant="outline">
                      {translations.common.createNewAccount}
                    </Button>
                  </Link>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
